import { useQuery } from "@apollo/client";
import { LEARNER_FEATURE_FLAGS } from "utility/graphQl/query";
import { LearnerFeatureFlag } from "../api/types";
import appInfo from "utility/app-info";

// Feature flag constants
export const FEATURE_FLAGS = {
  ORI_QA: "ori_qa",
  // Cycling placeholder statuses ("Letting it land…", …) in place of the dots
  ORI_WORDED_LOADING: "ori_worded_loading",
  // The post-assessment report, shown in place of the drop into the Index tab
  ASSESSMENT_REPORT: "assessment_report",
  // Lightweight Ori entry points that open the chat with the screen's context
  ORI_TAP_IN: "ori_tap_in",
  // Assessment answers saved as they are given, not only on "Save and exit"
  ASSESSMENT_AUTO_SAVE: "assessment_auto_save",
  // Ori opens a chat with tappable starter options, declared to Coach on __START__
  ORI_DYNAMIC_STARTERS: "ori_dynamic_starters",
  // "Share my score" on the report previews and shares an image of the score
  ASSESSMENT_REPORT_SHARE_IMAGE: "assessment_report_share_image",
  // The holiday-season door calendar: home card + /user/december-doors
  DECEMBER_DOORS: "december_doors",
} as const;

interface LearnerFeatureFlagsQueryResult {
  learner?: {
    id: string;
    featureFlags?: (LearnerFeatureFlag | null)[] | null;
  } | null;
}

interface UseFeatureFlagsReturn {
  isEnabled: (flagName: string) => boolean;
  loading: boolean;
  /**
   * Whether the flags are known yet, from cache or from the network.
   *
   * `loading` cannot answer that. The query runs `cache-and-network`, which
   * hands back cached flags immediately and *keeps* `loading` true while it
   * refreshes them — so a caller that waits for `loading` to clear withholds
   * a feature it already knows is enabled, then reveals it when the refresh
   * lands. That reads as a flicker, and it is worse on a warm cache than a
   * cold one. Branch on this instead.
   */
  resolved: boolean;
  error?: Error;
}

export default function useFeatureFlags(): UseFeatureFlagsReturn {
  const { data, loading, error } = useQuery<LearnerFeatureFlagsQueryResult>(
    LEARNER_FEATURE_FLAGS,
    {
      variables: {
        appInfo,
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const isEnabled = (flagName: string): boolean => {
    return (
      data?.learner?.featureFlags?.some(
        (flag: LearnerFeatureFlag | null) =>
          flag?.name === flagName && flag?.enabled
      ) ?? false
    );
  };

  // A failed query counts as resolved: the answer is "no flags", which
  // every flag reads as disabled. Treating it as unresolved would leave a
  // caller waiting on a request that is not coming back.
  const resolved = Boolean(data?.learner) || Boolean(error);

  return {
    isEnabled,
    loading,
    resolved,
    error: error ? new Error(error.message) : undefined,
  };
}
